import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

const InteractiveHeartModel = () => {
  const mountRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const [isRotating, setIsRotating] = useState(true);
  const [showBloodFlow, setShowBloodFlow] = useState(true);
  const [viewMode, setViewMode] = useState('external');
  const [pumpSpeed, setPumpSpeed] = useState(1.0);
  const [heartRate, setHeartRate] = useState(72);
  const [showLabels, setShowLabels] = useState(true);
  const [selectedStructure, setSelectedStructure] = useState<{ name: string; description: string } | null>(null);
  const [quizMode, setQuizMode] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showCrossSection, setShowCrossSection] = useState(false);
  const [crossSectionAxis, setCrossSectionAxis] = useState('y');
  const [crossSectionPosition, setCrossSectionPosition] = useState(0);
  const [showConditions, setShowConditions] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState(null);
  const [annotationLevel, setAnnotationLevel] = useState('basic');
  const [textureQuality, setTextureQuality] = useState('high');
  const [showElectricalSystem, setShowElectricalSystem] = useState(false);
  const [showCoronarySystem, setShowCoronarySystem] = useState(false);
  const [animationMode, setAnimationMode] = useState('normal');
  const [isVentricleBeating, setIsVentricleBeating] = useState(true);
  const [showInfo, setShowInfo] = useState(false);

  type QuizQuestion = {
    question: string;
    options: string[];
    correct: string;
    explanation: string;
  };

  // Quiz questions moved to component scope
  const quizQuestions: QuizQuestion[] = [
    {
      question: "Which chamber receives oxygenated blood from the lungs?",
      options: ["Right Atrium", "Left Atrium", "Right Ventricle", "Left Ventricle"],
      correct: "Left Atrium",
      explanation: "The left atrium receives oxygenated blood from the lungs via the pulmonary veins. This blood then flows through the mitral valve into the left ventricle."
    },
    {
      question: "What is the main function of the right ventricle?",
      options: [
        "Pump blood to the body",
        "Pump blood to the lungs",
        "Receive blood from the body",
        "Receive blood from the lungs"
      ],
      correct: "Pump blood to the lungs",
      explanation: "The right ventricle pumps deoxygenated blood through the pulmonary valve into the pulmonary artery and to the lungs where it becomes oxygenated."
    },
    {
      question: "Which vessel carries oxygenated blood to the body?",
      options: ["Pulmonary Artery", "Aorta", "Superior Vena Cava", "Pulmonary Vein"],
      correct: "Aorta",
      explanation: "The aorta is the largest artery in the body and carries oxygen-rich blood from the left ventricle to the rest of the body."
    },
    {
      question: "Which valve prevents backflow from the left ventricle to the left atrium?",
      options: ["Tricuspid Valve", "Pulmonary Valve", "Mitral Valve", "Aortic Valve"],
      correct: "Mitral Valve",
      explanation: "The mitral (bicuspid) valve is located between the left atrium and left ventricle, preventing backflow during ventricular contraction."
    }
  ];

  useEffect(() => {
    if (!mountRef.current) return;
    
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(
      35, // Reduced FOV for a better view
      mountRef.current.clientWidth / mountRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 5; // Moved camera further out
    camera.position.y = 0.3; // Slightly adjusted elevation
    
    // Renderer setup with enhanced settings
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true,
      logarithmicDepthBuffer: true 
    });
    renderer.setSize(676, 676); // Fixed size to match requirements
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    mountRef.current.appendChild(renderer.domElement);

    // Label renderer setup
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(676, 676); // Fixed size to match requirements
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0px';
    labelRenderer.domElement.style.left = '0px';
    labelRenderer.domElement.style.pointerEvents = 'none';
    mountRef.current.appendChild(labelRenderer.domElement);
    
    // Controls setup for better interaction
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.7;
    controls.autoRotate = isRotating;
    controls.autoRotateSpeed = 1.0;
    controls.enablePan = true;
    controls.minDistance = 3; // Adjusted minimum distance
    controls.maxDistance = 6; // Adjusted maximum distance
    controlsRef.current = controls;
    
    // Enhanced lighting setup
    const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    scene.add(directionalLight);
    
    const hemisphereLight = new THREE.HemisphereLight(0xffffbb, 0x080820, 0.6);
    scene.add(hemisphereLight);
    
    // Spotlight to create dramatic lighting
    const spotlight = new THREE.SpotLight(0xffffff, 0.8);
    spotlight.position.set(-5, 10, 5);
    spotlight.angle = Math.PI / 6;
    spotlight.penumbra = 0.2;
    spotlight.castShadow = true;
    scene.add(spotlight);
    
    // Heart group container
    const heartGroup = new THREE.Group();
    scene.add(heartGroup);
    
    // Material creation helper
    const createHeartMaterial = (color: THREE.ColorRepresentation, opacity = 0.85) => {
      return new THREE.MeshPhysicalMaterial({ 
        color: color,
        transparent: true,
        opacity: opacity,
        roughness: 0.5,
        metalness: 0.2,
        clearcoat: 0.3,
        clearcoatRoughness: 0.2,
        side: THREE.DoubleSide
      });
    };

    // Heart structures data
    const heartStructures = [
      {
        name: "Right Atrium",
        position: new THREE.Vector3(-0.6, 0.4, 0),
        color: 0x8a2be2,
        description: "Receives deoxygenated blood from the body through the superior and inferior vena cava. Has a thin muscular wall and contains the sinoatrial (SA) node, which is the heart's natural pacemaker.",
        size: 0.4,
        shape: 'sphere',
        opacity: 0.85
      },
      {
        name: "Left Atrium",
        position: new THREE.Vector3(0.6, 0.4, 0),
        color: 0xff3333,
        description: "Receives oxygenated blood from the lungs via the pulmonary veins. Slightly smaller than the right atrium but has a thicker wall.",
        size: 0.35,
        shape: 'sphere',
        opacity: 0.85
      },
      {
        name: "Right Ventricle",
        position: new THREE.Vector3(-0.6, -0.4, 0),
        color: 0x6495ed,
        description: "Pumps deoxygenated blood to the lungs through the pulmonary artery. Has a thinner wall compared to the left ventricle as it works against lower pressure.",
        size: 0.45,
        shape: 'sphere',
        opacity: 0.85
      },
      {
        name: "Left Ventricle",
        position: new THREE.Vector3(0.6, -0.4, 0),
        color: 0xdc143c,
        description: "The strongest chamber that pumps oxygenated blood to the entire body through the aorta. Has the thickest walls (about 1-1.5 cm) to generate sufficient pressure to circulate blood throughout the body.",
        size: 0.4,
        shape: 'sphere',
        opacity: 0.85
      },
      {
        name: "Aorta",
        position: new THREE.Vector3(0.2, 1.0, 0),
        color: 0xff0000,
        description: "The largest artery in the body, carrying oxygenated blood from the left ventricle to the rest of the body. Forms an arch that distributes blood to the upper body before descending to supply the lower body.",
        size: 0.2,
        shape: 'cylinder',
        rotation: new THREE.Euler(0.3, 0, 0.3),
        scale: new THREE.Vector3(1, 1.5, 1)
      },
      {
        name: "Pulmonary Artery",
        position: new THREE.Vector3(-0.2, 1.0, 0),
        color: 0x0000ff,
        description: "Carries deoxygenated blood from the right ventricle to the lungs where carbon dioxide is exchanged for oxygen. It's the only artery that carries deoxygenated blood.",
        size: 0.15,
        shape: 'cylinder',
        rotation: new THREE.Euler(0.3, 0, -0.3),
        scale: new THREE.Vector3(1, 1.3, 1)
      },
      {
        name: "Superior Vena Cava",
        position: new THREE.Vector3(-0.7, 0.9, 0),
        color: 0x1e90ff,
        description: "Large vein that carries deoxygenated blood from the upper half of the body to the right atrium.",
        size: 0.15,
        shape: 'cylinder',
        rotation: new THREE.Euler(0.2, 0, -0.1),
        scale: new THREE.Vector3(1, 1.2, 1)
      },
      {
        name: "Inferior Vena Cava",
        position: new THREE.Vector3(-0.5, -0.8, 0),
        color: 0x4169e1,
        description: "The largest vein in the body, carrying deoxygenated blood from the lower half of the body to the right atrium.",
        size: 0.2,
        shape: 'cylinder',
        rotation: new THREE.Euler(-0.3, 0, -0.1),
        scale: new THREE.Vector3(1, 1.0, 1)
      },
      {
        name: "Pulmonary Veins",
        position: new THREE.Vector3(0.8, 0.6, 0),
        color: 0xff6347,
        description: "Four veins (two from each lung) that carry oxygenated blood from the lungs to the left atrium. They are the only veins that carry oxygenated blood.",
        size: 0.12,
        shape: 'cylinder',
        rotation: new THREE.Euler(0.3, 0, 0.5),
        scale: new THREE.Vector3(1, 1.0, 1)
      },
      {
        name: "Tricuspid Valve",
        position: new THREE.Vector3(-0.8, 0, 0),
        color: 0xffffff,
        description: "Located between the right atrium and right ventricle, this valve has three cusps (flaps) and prevents backflow of blood into the right atrium during ventricular contraction.",
        size: 0.2,
        shape: 'custom',
        opacity: 0.9
      },
      {
        name: "Mitral Valve",
        position: new THREE.Vector3(0.8, 0, 0),
        color: 0xf5f5dc,
        description: "Located between the left atrium and left ventricle, this valve has two cusps and prevents backflow of blood into the left atrium during ventricular contraction. Also called the bicuspid valve.",
        size: 0.2,
        shape: 'custom',
        opacity: 0.9
      },
      {
        name: "Aortic Valve",
        position: new THREE.Vector3(0.5, 0.7, 0),
        color: 0xfffafa,
        description: "Located between the left ventricle and the aorta, this valve has three cusps and prevents backflow of blood into the left ventricle during diastole.",
        size: 0.15,
        shape: 'custom',
        opacity: 0.9
      },
      {
        name: "Pulmonary Valve",
        position: new THREE.Vector3(-0.5, 0.7, 0),
        color: 0xf0fff0,
        description: "Located between the right ventricle and pulmonary artery, this valve has three cusps and prevents backflow of blood into the right ventricle during diastole.",
        size: 0.15,
        shape: 'custom',
        opacity: 0.9
      }
    ];

    // Blood vessel paths
    const bloodVessels = [
      {
        name: "Aortic Path",
        path: [
          new THREE.Vector3(0.5, 0.7, 0),  // Start at aortic valve
          new THREE.Vector3(0.3, 1.3, 0),  // Continue to aortic arch
          new THREE.Vector3(0.1, 1.5, 0.2), // Curve around
          new THREE.Vector3(-0.2, 1.4, 0.3), // Continue arch
          new THREE.Vector3(-0.3, 1.0, 0.3)  // Descending aorta
        ],
        radius: 0.15,
        color: 0xff0000,
        description: "Path of blood through the aorta",
        flow: 'oxygenated'
      },
      {
        name: "Pulmonary Artery Path",
        path: [
          new THREE.Vector3(-0.5, 0.7, 0),  // Start at pulmonary valve
          new THREE.Vector3(-0.3, 1.2, 0),  // Continue to main pulmonary artery
          new THREE.Vector3(-0.2, 1.4, 0.2), // Begin branch
          new THREE.Vector3(-0.3, 1.5, 0.4)  // To lungs
        ],
        radius: 0.1,
        color: 0x0000ff,
        description: "Path of blood to the lungs",
        flow: 'deoxygenated'
      },
      {
        name: "Vena Cava Path",
        path: [
          new THREE.Vector3(-0.9, 1.5, 0),   // Upper body
          new THREE.Vector3(-0.9, 1.2, 0),   // Superior vena cava
          new THREE.Vector3(-0.8, 0.5, 0),   // Enter right atrium
        ],
        radius: 0.12,
        color: 0x1e90ff,
        description: "Path of deoxygenated blood returning from upper body",
        flow: 'deoxygenated'
      },
      {
        name: "Pulmonary Vein Path",
        path: [
          new THREE.Vector3(1.2, 1.0, 0.3),  // From lungs
          new THREE.Vector3(1.0, 0.8, 0),    // Pulmonary veins
          new THREE.Vector3(0.8, 0.5, 0),    // Enter left atrium
        ],
        radius: 0.08,
        color: 0xff6347,
        description: "Path of oxygenated blood from lungs",
        flow: 'oxygenated'
      }
    ];

    // Heart conditions
    const heartConditions = [
      {
        name: "Normal Heart",
        description: "A healthy heart with normal structure and function.",
        effects: {}
      },
      {
        name: "Atrial Fibrillation",
        description: "An irregular and often rapid heart rhythm that can lead to blood clots in the heart.",
        effects: {
          atriaRate: 6.0,
          atriaRhythm: "irregular",
          ventricleRate: 1.2,
          ventricleRhythm: "irregular"
        }
      },
      {
        name: "Mitral Valve Prolapse",
        description: "A condition in which the valve between the left atrium and left ventricle doesn't close properly.",
        effects: {
          mitralValve: "prolapsed",
          backflow: true
        }
      }
    ];

    // Collections for meshes
    const chambers: { [key: string]: THREE.Mesh } = {};
    const valves: { [key: string]: THREE.Mesh } = {};
    const vessels: { [key: string]: THREE.Mesh } = {};
    const structures: { [key: string]: THREE.Mesh } = {};
    const particles: { 
      bloodFlow: { [key: string]: THREE.Group },
      chamberFlows: { [key: string]: THREE.Group }
    } = {
      bloodFlow: {},
      chamberFlows: {}
    };
    
    // Create heart structures
    heartStructures.forEach(structure => {
      let mesh;
      
      if (structure.shape === 'sphere') {
        // For chambers
        const geometry = new THREE.SphereGeometry(structure.size, 32, 32);
        const material = createHeartMaterial(structure.color, structure.opacity);
        mesh = new THREE.Mesh(geometry, material);
        chambers[structure.name] = mesh;
      } 
      else if (structure.shape === 'cylinder') {
        // For blood vessels
        const geometry = new THREE.CylinderGeometry(structure.size, structure.size * 0.9, structure.size * 2, 32);
        const material = createHeartMaterial(structure.color, structure.opacity);
        mesh = new THREE.Mesh(geometry, material);
        vessels[structure.name] = mesh;
      }
      else if (structure.shape === 'custom') {
        if (structure.name.includes("Valve")) {
          // Custom geometry for valves
          const geometry = structure.name.includes("Mitral") ? 
            new THREE.TorusGeometry(structure.size, structure.size * 0.2, 16, 32, Math.PI) : 
            new THREE.TorusGeometry(structure.size, structure.size * 0.2, 16, 100);
          
          const material = new THREE.MeshPhysicalMaterial({ 
            color: structure.color,
            transparent: true,
            opacity: structure.opacity || 0.9,
            side: THREE.DoubleSide,
            roughness: 0.3,
            metalness: 0.1
          });
          
          mesh = new THREE.Mesh(geometry, material);
          valves[structure.name] = mesh;
        }
      }
      
      if (mesh) {
        // Set position and rotation
        mesh.position.copy(structure.position);
        if (structure.rotation) {
          mesh.rotation.copy(structure.rotation);
        }
        if (structure.scale) {
          mesh.scale.copy(structure.scale);
        }
        
        // Add user data for interaction
        mesh.userData = { 
          name: structure.name, 
          description: structure.description,
          type: structure.shape === 'sphere' ? 'chamber' : 
               structure.name.includes("Valve") ? 'valve' : 'vessel'
        };
        
        // Make objects castable for raycasting
        mesh.receiveShadow = true;
        mesh.castShadow = true;
        
        // Add to heart group
        heartGroup.add(mesh);
        
        // Add labels if enabled
        if (showLabels) {
          const labelDiv = document.createElement('div');
          labelDiv.className = 'label';
          labelDiv.textContent = structure.name;
          labelDiv.style.color = 'white';
          labelDiv.style.padding = '2px 5px';
          labelDiv.style.background = 'rgba(0, 0, 0, 0.7)';
          labelDiv.style.borderRadius = '3px';
          labelDiv.style.fontSize = '12px';
          labelDiv.style.pointerEvents = 'none';
          
          const label = new CSS2DObject(labelDiv);
          const labelPos = structure.position.clone();
          labelPos.y += structure.size + 0.2;
          label.position.copy(labelPos);
          mesh.add(label);
        }
      }
    });
    
    // Function to create coronary arteries
    function createCoronaryArteries(color: THREE.ColorRepresentation) {
      const group = new THREE.Group();
      
      // Main coronary artery
      const mainGeometry = new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3([
          new THREE.Vector3(0.3, 0.8, 0.4),
          new THREE.Vector3(0.4, 0.6, 0.5),
          new THREE.Vector3(0.6, 0.4, 0.4),
          new THREE.Vector3(0.7, 0.0, 0.3),
          new THREE.Vector3(0.5, -0.5, 0.2)
        ]),
        64,
        0.03,
        16,
        false
      );
      
      const mainMaterial = new THREE.MeshStandardMaterial({
        color: color,
        roughness: 0.3,
        metalness: 0.2
      });
      
      const mainCoronary = new THREE.Mesh(mainGeometry, mainMaterial);
      group.add(mainCoronary);
      
      // Left anterior descending branch
      const ladGeometry = new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3([
          new THREE.Vector3(0.6, 0.4, 0.4),
          new THREE.Vector3(0.5, 0.1, 0.5),
          new THREE.Vector3(0.4, -0.3, 0.4),
          new THREE.Vector3(0.3, -0.6, 0.2)
        ]),
        48,
        0.02,
        12,
        false
      );
      
      const ladCoronary = new THREE.Mesh(ladGeometry, mainMaterial);
      group.add(ladCoronary);
      
      // Circumflex branch
      const circumflexGeometry = new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3([
          new THREE.Vector3(0.4, 0.6, 0.5),
          new THREE.Vector3(0.7, 0.5, 0.4),
          new THREE.Vector3(0.9, 0.2, 0.2),
          new THREE.Vector3(0.8, -0.2, 0.1)
        ]),
        48,
        0.02,
        12,
        false
      );
      
      const circumflexCoronary = new THREE.Mesh(circumflexGeometry, mainMaterial);
      group.add(circumflexCoronary);
      
      // Right coronary artery
      const rcaGeometry = new THREE.TubeGeometry(
        new THREE.CatmullRomCurve3([
          new THREE.Vector3(0.3, 0.8, -0.2),
          new THREE.Vector3(0.1, 0.6, -0.4),
          new THREE.Vector3(-0.3, 0.3, -0.4),
          new THREE.Vector3(-0.6, 0.0, -0.3),
          new THREE.Vector3(-0.7, -0.4, -0.1)
        ]),
        64,
        0.025,
        12,
        false
      );
      
      const rcaMaterial = new THREE.MeshStandardMaterial({
        color: color,
        roughness: 0.3,
        metalness: 0.2
      });
      
      const rcaCoronary = new THREE.Mesh(rcaGeometry, rcaMaterial);
      group.add(rcaCoronary);
      
      // Smaller branches
      const addBranch = (start: number[], control1: number[], control2: number[], end: number[], radius = 0.015) => {
        const curve = new THREE.CubicBezierCurve3(
          new THREE.Vector3(...start),
          new THREE.Vector3(...control1),
          new THREE.Vector3(...control2),
          new THREE.Vector3(...end)
        );
        
        const branchGeometry = new THREE.TubeGeometry(curve, 24, radius, 8, false);
        const branch = new THREE.Mesh(branchGeometry, mainMaterial);
        group.add(branch);
      };
      
      // Add smaller branches
      addBranch([0.5, 0.1, 0.5], [0.6, 0.0, 0.6], [0.7, -0.1, 0.7], [0.8, -0.2, 0.8]);
      addBranch([0.4, -0.3, 0.4], [0.5, -0.4, 0.5], [0.6, -0.5, 0.4], [0.7, -0.6, 0.3]);
      addBranch([0.7, 0.5, 0.4], [0.8, 0.4, 0.5], [0.9, 0.3, 0.5], [1.0, 0.2, 0.4]);
      addBranch([-0.3, 0.3, -0.4], [-0.4, 0.2, -0.5], [-0.5, 0.1, -0.5], [-0.6, 0.0, -0.6]);
      
      group.userData = {
        name: "Coronary Arteries",
        description: "Network of blood vessels that supply oxygen-rich blood to the heart muscle itself."
      };
      
      return group;
    }
    
    // Add coronary arteries
    const coronarySystem = createCoronaryArteries(0xff4500);
    heartGroup.add(coronarySystem);
    coronarySystem.visible = showCoronarySystem;
    
    // Create electrical conduction system
    function createConductionSystem() {
      const group = new THREE.Group();
      
      // SA Node (sinoatrial node)
      const saNodeGeometry = new THREE.SphereGeometry(0.08, 16, 16);
      const conductionMaterial = new THREE.MeshStandardMaterial({
        color: 0xffff00,
        emissive: 0xffff00,
        emissiveIntensity: 0.3,
        transparent: true,
        opacity: 0.8
      });
      
      const saNode = new THREE.Mesh(saNodeGeometry, conductionMaterial);
      saNode.position.set(-0.8, 0.7, 0.2);
      saNode.userData = {
        name: "SA Node",
        description: "The heart's natural pacemaker that initiates each heartbeat."
      };
      group.add(saNode);
      
      // AV Node (atrioventricular node)
      const avNodeGeometry = new THREE.SphereGeometry(0.07, 16, 16);
      const avNode = new THREE.Mesh(avNodeGeometry, conductionMaterial);
      avNode.position.set(0, 0.1, 0.1);
      avNode.userData = {
        name: "AV Node",
        description: "Delays the electrical signal before passing it to the ventricles."
      };
      group.add(avNode);
      
      // Bundle of His
      const hisGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.3, 8);
      const hisMaterial = new THREE.MeshStandardMaterial({
        color: 0xffff77,
        emissive: 0xffff77,
        emissiveIntensity: 0.2,
        transparent: true,
        opacity: 0.7
      });
      
      const bundleOfHis = new THREE.Mesh(hisGeometry, hisMaterial);
      bundleOfHis.position.set(0, -0.1, 0.1);
      bundleOfHis.rotation.x = Math.PI / 2;
      bundleOfHis.userData = {
        name: "Bundle of His",
        description: "Conducts electrical impulses from the AV node to the ventricles."
      };
      group.add(bundleOfHis);
      
      // Purkinje Fibers - create a branching network
      const createPurkinjeBranch = (start: THREE.Vector3, end: THREE.Vector3, radius = 0.02, branchDepth = 0) => {
        if (branchDepth > 3) return null; // Limit recursion
        
        const midPoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        // Add some randomness to the midpoint
        midPoint.add(new THREE.Vector3(
          (Math.random() - 0.5) * 0.2,
          (Math.random() - 0.5) * 0.2,
          (Math.random() - 0.5) * 0.2
        ));
        
        const curve = new THREE.QuadraticBezierCurve3(start, midPoint, end);
        const branchGeometry = new THREE.TubeGeometry(curve, 8, radius, 6, false);
        const branchMaterial = new THREE.MeshStandardMaterial({
          color: 0xffff99,
          emissive: 0xffff99,
          emissiveIntensity: 0.2 - branchDepth * 0.05,
          transparent: true,
          opacity: 0.6 - branchDepth * 0.1
        });
        
        const branch = new THREE.Mesh(branchGeometry, branchMaterial);
        group.add(branch);

        // Generate sub-branches if not at depth limit
        if (branchDepth < 3) {
          // Calculate two random branch directions
          const dir = new THREE.Vector3().subVectors(end, start).normalize();
          const perpAxis = new THREE.Vector3(1, 0, 0).cross(dir).normalize();
          if (perpAxis.length() < 0.1) {
            perpAxis.set(0, 0, 1).cross(dir).normalize();
          }
          
          // Create sub-branches with smaller radius
          const newRadius = radius * 0.7;
          const branchLength = end.distanceTo(start) * 0.6;
          
          // First sub-branch
          const branch1End = new THREE.Vector3().copy(end);
          branch1End.addScaledVector(dir, branchLength * 0.5);
          branch1End.addScaledVector(perpAxis, branchLength * 0.3);
          createPurkinjeBranch(end, branch1End, newRadius, branchDepth + 1);
          
          // Second sub-branch
          const branch2End = new THREE.Vector3().copy(end);
          branch2End.addScaledVector(dir, branchLength * 0.5);
          branch2End.addScaledVector(perpAxis, -branchLength * 0.3);
          createPurkinjeBranch(end, branch2End, newRadius, branchDepth + 1);
        }
        
        return branch;
      };
      
      // Create the left and right bundle branches
      const leftBundleStart = new THREE.Vector3(0, -0.25, 0.1);
      const leftBundleEnd = new THREE.Vector3(0.3, -0.4, 0.1);
      const leftBundle = createPurkinjeBranch(leftBundleStart, leftBundleEnd, 0.025);
      if (leftBundle) {
        leftBundle.userData = {
          name: "Left Bundle Branch",
          description: "Conducts electrical impulses to the left ventricle."
        };
      }
      
      const rightBundleStart = new THREE.Vector3(0, -0.25, 0.1);
      const rightBundleEnd = new THREE.Vector3(-0.3, -0.4, 0.1);
      const rightBundle = createPurkinjeBranch(rightBundleStart, rightBundleEnd, 0.025);
      if (rightBundle) {
        rightBundle.userData = {
          name: "Right Bundle Branch",
          description: "Conducts electrical impulses to the right ventricle."
        };
      }
      
      // Create terminal Purkinje fibers
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const rightStart = new THREE.Vector3(-0.3, -0.4, 0.1);
        const rightEnd = new THREE.Vector3(
          -0.6 + Math.cos(angle) * 0.3,
          -0.7 + Math.sin(angle) * 0.3,
          0.1 + (Math.random() - 0.5) * 0.2
        );
        createPurkinjeBranch(rightStart, rightEnd, 0.015, 1);
        
        const leftStart = new THREE.Vector3(0.3, -0.4, 0.1);
        const leftEnd = new THREE.Vector3(
          0.6 + Math.cos(angle + Math.PI) * 0.3,
          -0.7 + Math.sin(angle + Math.PI) * 0.3,
          0.1 + (Math.random() - 0.5) * 0.2
        );
        createPurkinjeBranch(leftStart, leftEnd, 0.015, 1);
      }
      
      // Create electrical impulse animation
      const impulseGeometry = new THREE.SphereGeometry(0.05, 16, 16);
      const impulseMaterial = new THREE.MeshBasicMaterial({
        color: 0xffff00,
        transparent: true,
        opacity: 0.7
      });
      
      const impulse = new THREE.Mesh(impulseGeometry, impulseMaterial);
      impulse.position.copy(saNode.position);
      impulse.visible = false; // Start hidden
      group.add(impulse);
      
      group.userData = {
        impulse: impulse,
        saNode: saNode,
        avNode: avNode
      };
      
      return group;
    }
    
    // Add electrical conduction system
    const electricalSystem = createConductionSystem();
    heartGroup.add(electricalSystem);
    electricalSystem.visible = showElectricalSystem;
    
    // Create blood flow particles
    function createBloodParticles() {
      const particleGroups: { [key: string]: THREE.Group } = {};
      
      bloodVessels.forEach(vessel => {
        const particles = new THREE.Group();
        const particleCount = 20;
        const particleGeometry = new THREE.SphereGeometry(0.04, 8, 8);
        const particleMaterial = new THREE.MeshBasicMaterial({
          color: vessel.flow === 'oxygenated' ? 0xff0000 : 0x0000ff,
          transparent: true,
          opacity: 0.7
        });
        
        // Create particles along the path
        for (let i = 0; i < particleCount; i++) {
          const particle = new THREE.Mesh(particleGeometry, particleMaterial);
          
          // Distribute particles along the path
          const pathPos = i / particleCount;
          const pointIndex = Math.floor(pathPos * (vessel.path.length - 1));
          const nextPointIndex = Math.min(pointIndex + 1, vessel.path.length - 1);
          const pointPos = (pathPos * (vessel.path.length - 1)) % 1;
          
          const position = new THREE.Vector3().lerpVectors(
            vessel.path[pointIndex],
            vessel.path[nextPointIndex],
            pointPos
          );
          
          particle.position.copy(position);
          particle.userData = {
            startPoint: pointIndex,
            progress: pointPos,
            speed: 0.005 + Math.random() * 0.005, // Varied speeds
            vessel: vessel
          };
          
          particles.add(particle);
        }
        
        particleGroups[vessel.name] = particles;
        heartGroup.add(particles);
      });
      
      return particleGroups;
    }
    
    // Add blood flow particles
    const bloodParticles = createBloodParticles();
    particles.bloodFlow = bloodParticles;
    
    // Create blood flow in chambers
    function createChamberBloodFlow() {
      const chamberFlows: { [key: string]: THREE.Group } = {};
      const colors: { [key: string]: number } = {
        "Right Atrium": 0x0000ff,
        "Right Ventricle": 0x0000ff,
        "Left Atrium": 0xff0000,
        "Left Ventricle": 0xff0000
      };
      
      Object.keys(chambers).forEach(name => {
        if (!colors[name]) return;
        
        const particleGroup = new THREE.Group();
        const particleCount = 30;
        const chamber = chambers[name];
        const color = colors[name];
        
        // Create chamber particles
        for (let i = 0; i < particleCount; i++) {
          const size = 0.03 + Math.random() * 0.03;
          const particleGeometry = new THREE.SphereGeometry(size, 8, 8);
          const particleMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.6
          });
          
          const particle = new THREE.Mesh(particleGeometry, particleMaterial);
          
          // Random position within the chamber (spherical distribution)
          const radius = (chamber.geometry as THREE.SphereGeometry).parameters.radius * 0.7 * Math.random();
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.random() * Math.PI;
          
          particle.position.set(
            radius * Math.sin(phi) * Math.cos(theta),
            radius * Math.sin(phi) * Math.sin(theta),
            radius * Math.cos(phi)
          );
          
          // Store original position for animation
          particle.userData = {
            originalPosition: particle.position.clone(),
            phase: Math.random() * Math.PI * 2,
            speed: 0.02 + Math.random() * 0.02,
            amplitude: 0.1 + Math.random() * 0.1
          };
          
          particleGroup.add(particle);
        }
        
        chambers[name].add(particleGroup);
        chamberFlows[name] = particleGroup;
      });
      
      return chamberFlows;
    }
    
    // Add chamber blood flow
    const chamberBloodFlows = createChamberBloodFlow();
    particles.chamberFlows = chamberBloodFlows;
    
    // Animate blood particles
    function animateBloodParticles(deltaTime: number) {
      if (!showBloodFlow) return;
      
      // Animate flow in vessels
      Object.values(particles.bloodFlow).forEach(group => {
        group.children.forEach(particle => {
          const userData = particle.userData;
          const vessel = userData.vessel;
          
          // Update progress along path
          userData.progress += userData.speed * pumpSpeed;
          if (userData.progress >= 1) {
            userData.startPoint++;
            userData.progress = 0;
            
            // Reset if reached end of path
            if (userData.startPoint >= vessel.path.length - 1) {
              userData.startPoint = 0;
            }
          }
          
          // Calculate new position
          const pointIndex = userData.startPoint;
          const nextPointIndex = Math.min(pointIndex + 1, vessel.path.length - 1);
          
          const position = new THREE.Vector3().lerpVectors(
            vessel.path[pointIndex],
            vessel.path[nextPointIndex],
            userData.progress
          );
          
          particle.position.copy(position);
        });
      });
      
      // Animate flow in chambers
      Object.keys(particles.chamberFlows).forEach(chamberName => {
        const particleGroup = particles.chamberFlows[chamberName];
        const shouldAnimate = isVentricleBeating && chamberName.includes("Ventricle");
        
        particleGroup.children.forEach(particle => {
          const userData = particle.userData;
          
          // Update phase
          userData.phase += userData.speed * pumpSpeed * deltaTime;
          
          // Calculate new position with some swirling motion
          const originalPos = userData.originalPosition;
          const newPos = originalPos.clone();
          
          // Add motion based on sine wave + swirl
          if (shouldAnimate) {
            const swirl = new THREE.Vector3(
              Math.sin(userData.phase) * userData.amplitude,
              Math.cos(userData.phase) * userData.amplitude,
              Math.sin(userData.phase + Math.PI/4) * userData.amplitude
            );
            newPos.add(swirl);
          }
          
          particle.position.copy(newPos);
        });
      });
    }
    
    // Animate valve opening/closing
    function animateValves(time: number) {
      if (!isVentricleBeating) return;
      
      const heartCycle = (time * pumpSpeed) % 1;
      const systole = heartCycle < 0.3; // Ventricle contraction
      
      // Tricuspid and Mitral valves - open during diastole (ventricle filling)
      const atrioventricularValves = [valves["Tricuspid Valve"], valves["Mitral Valve"]];
      atrioventricularValves.forEach(valve => {
        if (!valve) return;
        
        // Scale to simulate opening/closing
        const scale = systole ? 0.7 : 1.0;
        valve.scale.set(scale, 1.0, scale);
        
        // Rotate slightly based on cycle
        valve.rotation.x = Math.sin(heartCycle * Math.PI * 2) * 0.2;
      });
      
      // Aortic and Pulmonary valves - open during systole (ventricle emptying)
      const semilunarValves = [valves["Aortic Valve"], valves["Pulmonary Valve"]];
      semilunarValves.forEach(valve => {
        if (!valve) return;
        
        // Scale to simulate opening/closing (opposite of AV valves)
        const scale = systole ? 1.0 : 0.7;
        valve.scale.set(scale, 1.0, scale);
        
        // Rotate slightly based on cycle
        valve.rotation.x = Math.sin((heartCycle + 0.5) * Math.PI * 2) * 0.2;
      });
    }
    
    // Animate chamber contraction
    function animateChambers(time: number) {
      if (!isVentricleBeating) return;
      
      const heartCycle = (time * pumpSpeed) % 1;
      
      // Atria contract first, then ventricles
      const atrialSystole = heartCycle > 0.8 || heartCycle < 0.1;
      const ventricularSystole = heartCycle >= 0.1 && heartCycle < 0.4;
      
      // Animate atria
      ["Right Atrium", "Left Atrium"].forEach(name => {
        const chamber = chambers[name];
        if (!chamber) return;
        
        // Calculate scale factor - contract during atrial systole
        const basePulse = Math.sin(heartCycle * Math.PI * 2 - Math.PI/2) * 0.1 + 0.9;
        const scaleFactor = atrialSystole ? basePulse * 0.8 + 0.2 : 1.0;
        chamber.scale.set(scaleFactor, scaleFactor, scaleFactor);
      });
      
      // Animate ventricles - with more pronounced contraction
      ["Right Ventricle", "Left Ventricle"].forEach(name => {
        const chamber = chambers[name];
        if (!chamber) return;
        
        // Calculate scale factor - contract during ventricular systole
        const basePulse = Math.sin((heartCycle - 0.25) * Math.PI * 2) * 0.15 + 0.85;
        const scaleFactor = ventricularSystole ? basePulse * 0.7 + 0.3 : 1.0;
        chamber.scale.set(scaleFactor, scaleFactor, scaleFactor);
      });
    }
    
    // Animate electrical conduction system
    function animateElectricalSystem(time: number) {
      if (!showElectricalSystem || !isVentricleBeating) return;
      
      const heartCycle = (time * pumpSpeed) % 1;
      const impulse = electricalSystem.userData.impulse;
      
      // Define conduction path points
      const conductionPath = [
        electricalSystem.userData.saNode.position.clone(), // SA Node
        new THREE.Vector3(0, 0.1, 0.1),                   // AV Node
        new THREE.Vector3(0, -0.25, 0.1),                 // Bundle of His
        new THREE.Vector3(0.3, -0.4, 0.1),                // Left bundle branch
        new THREE.Vector3(-0.3, -0.4, 0.1),               // Right bundle branch
        new THREE.Vector3(0.6, -0.7, 0.1),                // Left Purkinje
        new THREE.Vector3(-0.6, -0.7, 0.1)                // Right Purkinje
      ];
      
      // Hide impulse when not in active phase
      if (heartCycle < 0.8) {
        // Map heartCycle to progress along conduction path
        const pathProgress = (heartCycle / 0.8) * (conductionPath.length - 1);
        const segmentIndex = Math.min(Math.floor(pathProgress), conductionPath.length - 2);
        const segmentProgress = pathProgress - segmentIndex;
        
        // Interpolate between path points
        const position = new THREE.Vector3().lerpVectors(
          conductionPath[segmentIndex],
          conductionPath[segmentIndex + 1],
          segmentProgress
        );
        
        impulse.position.copy(position);
        impulse.visible = true;
        
        // Pulse effect
        const scale = 0.8 + Math.sin(time * 10) * 0.2;
        impulse.scale.set(scale, scale, scale);
      } else {
        impulse.visible = false;
      }
      
      // Highlight nodes during activation
      const saNode = electricalSystem.userData.saNode;
      const avNode = electricalSystem.userData.avNode;
      
      // SA node activates at the start of cycle
      if (heartCycle < 0.1 || heartCycle > 0.9) {
        saNode.material.emissiveIntensity = 0.8;
        saNode.scale.set(1.2, 1.2, 1.2);
      } else {
        saNode.material.emissiveIntensity = 0.3;
        saNode.scale.set(1.0, 1.0, 1.0);
      }
      
      // AV node activates slightly later
      if (heartCycle > 0.1 && heartCycle < 0.3) {
        avNode.material.emissiveIntensity = 0.8;
        avNode.scale.set(1.2, 1.2, 1.2);
      } else {
        avNode.material.emissiveIntensity = 0.3;
        avNode.scale.set(1.0, 1.0, 1.0);
      }
    }
    
    // Animate coronary system
    function animateCoronarySystem(time: number) {
      if (!showCoronarySystem || !coronarySystem) return;
      
      const heartCycle = (time * pumpSpeed) % 1;
      const systole = heartCycle >= 0.1 && heartCycle < 0.4;
      
      // During systole, coronary arteries are compressed slightly
      const scale = systole ? 0.95 : 1.0;
      coronarySystem.scale.set(scale, scale, scale);
    }
    
    // Create cross-section plane
    let clipPlane: THREE.Plane | undefined;
    let clipPlaneHelper: THREE.PlaneHelper | undefined;
    
    function updateCrossSection() {
      if (!clipPlane) {
        // Create clipping plane if it doesn't exist
        clipPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        
        // Add clipping to all materials
        scene.traverse((object) => {
          if (object instanceof THREE.Mesh) {
            if (object.material) {
              if (Array.isArray(object.material)) {
                object.material.forEach(mat => {
                  if (clipPlane) {  // Add type guard
                    mat.clippingPlanes = [clipPlane];
                    mat.clipShadows = true;
                  }
                });
              } else {
                if (clipPlane) {  // Add type guard
                  object.material.clippingPlanes = [clipPlane];
                  object.material.clipShadows = true;
                }
              }
            }
          }
        });
        
        // Create helper to visualize the plane
        clipPlaneHelper = new THREE.PlaneHelper(clipPlane, 10, 0xffff00);
        scene.add(clipPlaneHelper);
      }
      
      // Update clip plane normal based on axis
      let normal = new THREE.Vector3(0, 0, 0);
      if (crossSectionAxis === 'x') normal.set(1, 0, 0);
      else if (crossSectionAxis === 'y') normal.set(0, 1, 0);
      else normal.set(0, 0, 1);
      
      clipPlane.normal.copy(normal);
      clipPlane.constant = crossSectionPosition;
      
      // Update renderer clipping
      renderer.clippingPlanes = showCrossSection ? [clipPlane] : [];
      if (clipPlaneHelper) {  // Add type guard
        clipPlaneHelper.visible = showCrossSection;
      }
    }
    
    // Handle window resize
    function handleResize() {
      if (!mountRef.current) return;
      
      const width = 676;
      const height = 676;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
      labelRenderer.setSize(width, height);
    }
    
    window.addEventListener('resize', handleResize);
    
    // Raycaster for object selection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    
    function handleMouseMove(event: MouseEvent) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Raycast to find intersected objects
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(heartGroup.children, true);
      
      if (intersects.length > 0) {
        const object = intersects[0].object;
        
        if (object.userData && object.userData.name && object.userData.description) {
          document.body.style.cursor = 'pointer';
          // Highlight object
          const mesh = object as THREE.Mesh;
          if (mesh.material && !object.userData.isHighlighted) {
            const material = mesh.material as THREE.MeshStandardMaterial;
            object.userData.originalColor = material.color.clone();
            material.color.lerp(new THREE.Color(0xffffff), 0.3);
            object.userData.isHighlighted = true;
          }
        } else {
          document.body.style.cursor = 'auto';
        }
      } else {
        document.body.style.cursor = 'auto';
      }
      
      // Reset highlight on objects that are no longer intersected
      heartGroup.traverse((child) => {
        if (child.userData && child.userData.isHighlighted) {
          if (!intersects.find(intersect => intersect.object === child)) {
            const mesh = child as THREE.Mesh;
            if (mesh.material && child.userData.originalColor) {
              const material = mesh.material as THREE.MeshStandardMaterial;
              material.color.copy(child.userData.originalColor);
              child.userData.isHighlighted = false;
            }
          }
        }
      });
    }
    
    function handleClick(event: MouseEvent) {
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Raycast to find intersected objects
      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(heartGroup.children, true);
      
      if (intersects.length > 0) {
        const object = intersects[0].object;
        if (object.userData && object.userData.name && object.userData.description) {
          setSelectedStructure({
            name: object.userData.name,
            description: object.userData.description
          });
          
          // For quiz mode
          if (quizMode && quizQuestions[currentQuestion]) {
            const question = quizQuestions[currentQuestion];
            if (object.userData.name === question.correct) {
              alert("Correct! " + question.explanation);
              setCurrentQuestion((prev) => (prev + 1) % quizQuestions.length);
            } else {
              alert("Incorrect. Try again.");
            }
          }
        }
      }
    }
    
    // Add event listeners
    renderer.domElement.addEventListener('mousemove', handleMouseMove);
    renderer.domElement.addEventListener('click', handleClick);
    
    // Create animation loop
    const clock = new THREE.Clock();
    let lastTime = 0;
    
    const animate = () => {
      const time = clock.getElapsedTime();
      const deltaTime = time - lastTime;
      lastTime = time;
      
      if (controlsRef.current) {
        controlsRef.current.autoRotate = isRotating;
        controlsRef.current.update();
      }
      
      // Update heart rate based animations
      const effectiveHeartRate = heartRate * pumpSpeed;
      const beatsPerSecond = effectiveHeartRate / 60;
      const normalizedTime = time * beatsPerSecond;
      
      // Run animations
      if (isVentricleBeating) {
        animateValves(normalizedTime);
        animateChambers(normalizedTime);
        animateBloodParticles(deltaTime);
        animateElectricalSystem(normalizedTime);
        animateCoronarySystem(normalizedTime);
      }
      
      // Update cross section if active
      if (showCrossSection) {
        updateCrossSection();
      }
      
      // Apply animations based on view mode
      if (viewMode === 'xray') {
        heartGroup.traverse((object) => {
          const mesh = object as THREE.Mesh;
          if (mesh.material) {
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach(mat => {
                mat.opacity = 0.6;
                mat.transparent = true;
              });
            } else {
              mesh.material.opacity = 0.6;
              mesh.material.transparent = true;
            }
          }
        });
      }
      
      // Render the scene
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
      
      requestAnimationFrame(animate);
    };
    
    // Start animation
    animate();
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.domElement.removeEventListener('mousemove', handleMouseMove);
      renderer.domElement.removeEventListener('click', handleClick);
      
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
        mountRef.current.removeChild(labelRenderer.domElement);
      }
      
      // Dispose of geometries and materials
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(mat => mat.dispose());
            } else {
              object.material.dispose();
            }
          }
        }
      });
    };
  }, [isRotating, showBloodFlow, viewMode, pumpSpeed, heartRate, showLabels, 
      quizMode, currentQuestion, showCrossSection, crossSectionAxis, 
      crossSectionPosition, showConditions, selectedCondition, 
      annotationLevel, textureQuality, showElectricalSystem, 
      showCoronarySystem, animationMode, isVentricleBeating]);

  // Create UI controls
  return (
    <div className="flex flex-col h-screen bg-gray-900">
      {/* Top Controls Bar */}
      <div className="flex flex-wrap gap-4 p-4 bg-gray-800 text-white shadow-lg">
        <div className="flex items-center space-x-2 bg-gray-700 p-2 rounded-lg">
          <label htmlFor="viewMode" className="font-medium">View Mode:</label>
          <select 
            id="viewMode" 
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="bg-gray-600 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="external">External</option>
            <option value="xray">X-Ray</option>
            <option value="internal">Internal</option>
          </select>
        </div>
        
        <div className="flex items-center space-x-2 bg-gray-700 p-2 rounded-lg">
          <label htmlFor="heartRate" className="font-medium">Heart Rate:</label>
          <input 
            type="range" 
            id="heartRate" 
            min="40" 
            max="180" 
            value={heartRate} 
            onChange={(e) => setHeartRate(parseInt(e.target.value))}
            className="w-32 accent-blue-500"
          />
          <span className="min-w-[3rem]">{heartRate} bpm</span>
        </div>
        
        <div className="flex items-center space-x-4 bg-gray-700 p-2 rounded-lg">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={isRotating} 
              onChange={() => setIsRotating(!isRotating)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Auto-Rotate</span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={isVentricleBeating} 
              onChange={() => setIsVentricleBeating(!isVentricleBeating)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Beating</span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={showBloodFlow} 
              onChange={() => setShowBloodFlow(!showBloodFlow)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Blood Flow</span>
          </label>
        </div>
        
        <div className="flex items-center space-x-4 bg-gray-700 p-2 rounded-lg">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={showLabels} 
              onChange={() => setShowLabels(!showLabels)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Labels</span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={showCrossSection} 
              onChange={() => setShowCrossSection(!showCrossSection)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Cross-Section</span>
          </label>
          
          {showCrossSection && (
            <div className="flex items-center space-x-2">
              <select 
                value={crossSectionAxis}
                onChange={(e) => setCrossSectionAxis(e.target.value)}
                className="bg-gray-600 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="x">X-Axis</option>
                <option value="y">Y-Axis</option>
                <option value="z">Z-Axis</option>
              </select>
              
              <input 
                type="range" 
                min="-2" 
                max="2" 
                step="0.1" 
                value={crossSectionPosition}
                onChange={(e) => setCrossSectionPosition(parseFloat(e.target.value))}
                className="w-32 accent-blue-500"
              />
            </div>
          )}
        </div>
        
        <div className="flex items-center space-x-4 bg-gray-700 p-2 rounded-lg">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={showElectricalSystem} 
              onChange={() => setShowElectricalSystem(!showElectricalSystem)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Electrical System</span>
          </label>
          
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={showCoronarySystem} 
              onChange={() => setShowCoronarySystem(!showCoronarySystem)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Coronary System</span>
          </label>
        </div>
        
        <div className="flex items-center space-x-4 bg-gray-700 p-2 rounded-lg">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input 
              type="checkbox" 
              checked={quizMode} 
              onChange={() => setQuizMode(!quizMode)}
              className="w-4 h-4 accent-blue-500"
            />
            <span>Quiz Mode</span>
          </label>
          
          <select 
            value={annotationLevel}
            onChange={(e) => setAnnotationLevel(e.target.value)}
            className="bg-gray-600 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="basic">Basic</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
      </div>
      
      {/* Main 3D View */}
      <div
        ref={mountRef}
        style={{
          width: 676,
          height: 676,
          position: 'relative',
          margin: '0 auto',
          background: '#000',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Selected Structure Info Panel */}
        {selectedStructure && (
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-2xl max-w-md transform transition-all duration-300 hover:scale-105">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-gray-800">{selectedStructure.name}</h3>
              <button 
                onClick={() => setSelectedStructure(null)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-gray-600 leading-relaxed">{selectedStructure.description}</p>
          </div>
        )}
        
        {/* Quiz Panel */}
        {quizMode && quizQuestions[currentQuestion] && (
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-2xl max-w-md transform transition-all duration-300">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-800 mb-2">Question {currentQuestion + 1}</h3>
              <p className="text-gray-600">{quizQuestions[currentQuestion].question}</p>
            </div>
            <div className="space-y-3">
              {quizQuestions[currentQuestion].options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (option === quizQuestions[currentQuestion].correct) {
                      alert("Correct! " + quizQuestions[currentQuestion].explanation);
                      setCurrentQuestion((prev) => (prev + 1) % quizQuestions.length);
                    } else {
                      alert("Incorrect. Try again.");
                    }
                  }}
                  className="w-full text-left p-3 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 text-gray-700"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveHeartModel;